(function () {
  const STORAGE_KEY = "products-wishlist";

  const readWishlistSet = () => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      const arr = raw ? JSON.parse(raw) : [];
      if (!Array.isArray(arr)) return new Set();
      return new Set(arr.map(String));
    } catch {
      return new Set();
    }
  };

  const writeWishlistSet = (set) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify([...set]));
    } catch {
      // ignore
    }
  };

  const initWishlist = (root) => {
    const buttons = root.querySelectorAll(
      ".product-card__wishlist[data-product-id]",
    );
    if (!buttons.length) return;

    const wishlist = readWishlistSet();

    const syncButton = (btn) => {
      const id = String(btn.dataset.productId);
      const active = wishlist.has(id);
      btn.classList.toggle("is-active", active);
      btn.setAttribute("aria-pressed", active ? "true" : "false");
    };

    buttons.forEach((btn) => syncButton(btn));

    buttons.forEach((btn) => {
      btn.addEventListener("click", (e) => {
        e.preventDefault();
        e.stopPropagation();
        const id = String(btn.dataset.productId);
        if (wishlist.has(id)) wishlist.delete(id);
        else wishlist.add(id);
        writeWishlistSet(wishlist);
        syncButton(btn);
      });
    });
  };

  const createHoverZones = (card, count) => {
    const gallery = card.querySelector(".product-card__gallery");
    if (!gallery || count < 2) return [];

    const wrapper = document.createElement("div");
    wrapper.className = "product-card__hover-zones";

    for (let i = 0; i < count; i += 1) {
      const zone = document.createElement("div");
      zone.className = "product-card__hover-zone";
      zone.dataset.index = String(i);
      wrapper.appendChild(zone);
    }

    gallery.appendChild(wrapper);
    return wrapper.querySelectorAll(".product-card__hover-zone");
  };

  const initCatalogImageSwap = (root) => {
    if (!root) return;

    const cards = Array.from(
      root.matches?.(".product-card--catalog")
        ? [root]
        : root.querySelectorAll(".product-card--catalog"),
    ).filter((card) => !card.hasAttribute("data-catalog-swap-bound"));

    cards.forEach((card) => {
      const img = card.querySelector(".product-card__main-image--primary");
      if (!img?.dataset.hoverSrc) return;

      card.dataset.catalogSwapBound = "true";

      const primarySrc = img.getAttribute("src");
      const primarySrcset = img.getAttribute("srcset") || "";
      const hoverSrc = img.dataset.hoverSrc;
      const hoverSrcset = img.dataset.hoverSrcset || "";

      const showHoverImage = () => {
        if (card.dataset.colorSelected === "true") return;
        card.classList.add("is-image-swapping");
        img.setAttribute("src", hoverSrc);
        if (hoverSrcset) img.setAttribute("srcset", hoverSrcset);
        else img.removeAttribute("srcset");
      };

      const showPrimaryImage = () => {
        if (card.dataset.colorSelected === "true") {
          const selectedSrc = card.dataset.colorImageSrc;
          const selectedSrcset = card.dataset.colorImageSrcset || "";
          if (selectedSrc) {
            card.classList.remove("is-image-swapping");
            img.setAttribute("src", selectedSrc);
            if (selectedSrcset) img.setAttribute("srcset", selectedSrcset);
            else img.removeAttribute("srcset");
          }
          return;
        }
        card.classList.remove("is-image-swapping");
        if (primarySrc) img.setAttribute("src", primarySrc);
        if (primarySrcset) img.setAttribute("srcset", primarySrcset);
        else img.removeAttribute("srcset");
      };

      card.addEventListener("mouseenter", showHoverImage);
      card.addEventListener("mouseleave", showPrimaryImage);
    });
  };

  const applyCardImage = (card, src, srcset) => {
    if (!src) return false;

    const primary = card.querySelector(".product-card__main-image--primary");
    if (primary) {
      card.classList.remove("is-image-swapping");
      primary.setAttribute("src", src);
      if (srcset) primary.setAttribute("srcset", srcset);
      else primary.removeAttribute("srcset");

      // Editorial galleries stack multiple images — keep the primary visible.
      if (!card.classList.contains("product-card--catalog")) {
        const galleryImages = card.querySelectorAll(".product-card__main-image");
        galleryImages.forEach((img, i) => {
          if (i === 0) {
            img.style.setProperty("opacity", "1", "important");
            img.style.setProperty("z-index", "3", "important");
          } else {
            img.style.setProperty("opacity", "0", "important");
            img.style.setProperty("z-index", "1", "important");
          }
        });
      }
      return true;
    }
    return false;
  };

  const initCardColorSwatches = (root) => {
    if (!root) return;

    const cards = Array.from(
      root.matches?.(".product-card")
        ? [root]
        : root.querySelectorAll(".product-card"),
    ).filter((card) => !card.hasAttribute("data-swatches-bound"));

    cards.forEach((card) => {
      const swatches = card.querySelectorAll("[data-product-card-swatch]");
      if (!swatches.length) return;

      card.dataset.swatchesBound = "true";

      swatches.forEach((swatch) => {
        swatch.addEventListener("click", (event) => {
          event.preventDefault();
          event.stopPropagation();

          const src = swatch.dataset.imageSrc || "";
          const srcset = swatch.dataset.imageSrcset || "";

          swatches.forEach((item) => {
            item.classList.toggle("is-active", item === swatch);
            item.setAttribute(
              "aria-pressed",
              item === swatch ? "true" : "false",
            );
          });

          if (src) {
            card.dataset.colorSelected = "true";
            card.dataset.colorImageSrc = src;
            card.dataset.colorImageSrcset = srcset;
            applyCardImage(card, src, srcset);
          }
        });
      });
    });
  };

  const initProductCardGallery = (root) => {
    if (!root) return;

    const cards = Array.from(
      root.matches?.(".product-card")
        ? [root]
        : root.querySelectorAll(".product-card"),
    ).filter(
      (card) =>
        !card.hasAttribute("data-gallery-enhanced") &&
        !card.classList.contains("product-card--catalog"),
    );

    cards.forEach((card) => {
      card.dataset.galleryEnhanced = "true";

      const galleryImages = card.querySelectorAll(".product-card__main-image");
      if (!galleryImages.length) return;

      let hoverZones = card.querySelectorAll(".product-card__hover-zone");
      if (!hoverZones.length && galleryImages.length > 1) {
        hoverZones = createHoverZones(card, galleryImages.length);
      }

      if (!hoverZones.length) return;

      const activateImage = (index) => {
        if (index < 0 || index >= galleryImages.length) return;

        galleryImages.forEach((img, i) => {
          if (i === index) {
            img.style.setProperty("opacity", "1", "important");
            img.style.setProperty("z-index", "3", "important");
          } else {
            img.style.setProperty("opacity", "0", "important");
            img.style.setProperty("z-index", i === 0 ? "2" : "1", "important");
          }
        });
      };

      hoverZones.forEach((zone) => {
        zone.addEventListener("mouseenter", () => {
          if (card.dataset.colorSelected === "true") return;
          const index = parseInt(zone.dataset.index, 10);
          activateImage(index);
        });
      });

      card.addEventListener("mouseleave", () => {
        if (card.dataset.colorSelected === "true") {
          const selectedSrc = card.dataset.colorImageSrc;
          const selectedSrcset = card.dataset.colorImageSrcset || "";
          if (selectedSrc) applyCardImage(card, selectedSrc, selectedSrcset);
          return;
        }
        activateImage(0);
      });
    });
  };

  const initSection = (root) => {
    if (!root) return;
    initWishlist(root);
    initCatalogImageSwap(root);
    initProductCardGallery(root);
    initCardColorSwatches(root);
  };

  const initAll = () => {
    document.querySelectorAll("[data-products-root]").forEach(initSection);
    initCatalogImageSwap(document.body);
    initProductCardGallery(document.body);
    initCardColorSwatches(document.body);
  };

  const observeAddedCards = () => {
    if (typeof MutationObserver === "undefined") return;

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (!(node instanceof Element)) return;
          if (node.matches(".product-card")) {
            if (node.classList.contains("product-card--catalog")) {
              initCatalogImageSwap(node);
            } else {
              initProductCardGallery(node);
            }
            initWishlist(node);
            initCardColorSwatches(node);
          }
          if (node.querySelector?.(".product-card")) {
            initCatalogImageSwap(node);
            initProductCardGallery(node);
            initWishlist(node);
            initCardColorSwatches(node);
          }
        });
      });
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });
  };

  document.addEventListener("shopify:section:load", (event) => {
    const root = event?.target?.closest?.("[data-products-root]");
    if (root) initSection(root);
    else {
      initCatalogImageSwap(event.target);
      initProductCardGallery(event.target);
      initCardColorSwatches(event.target);
    }
  });

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => {
      initAll();
      observeAddedCards();
    });
  } else {
    initAll();
    observeAddedCards();
  }
})();
